import { COMPARISON_BAR_CHART_TEST_IDS } from '@/constants/data-testids.ts';

type CustomComparisonBarProps = {
  x: number;
  y: number;
  width: number;
  height: number;
  variant?: 'success' | 'declined';
  payload: {
    name: string;
    currentValue: number;
    previousValue: number;
  };
  barsColor: {
    currentColor: string;
    previousColor: string;
  };
  background: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  index: number;
  hoveredIndex: number | null;
  setHoveredIndex: (index: number | null) => void;
  isEditCellSkeleton: boolean;
};

export const CustomComparisonBar = (props: CustomComparisonBarProps) => {
  const {
    x,
    y,
    height,
    payload: { currentValue, previousValue },
    variant,
    barsColor: { currentColor, previousColor },
    background,
    index,
    hoveredIndex,
    setHoveredIndex,
    isEditCellSkeleton = false,
  } = props;

  const isHovered = hoveredIndex === index;
  const isDimmed = hoveredIndex !== null && hoveredIndex !== index;

  const maxBarWidth = background.width;

  const prevScale = 100 / previousValue;
  const currScale = 100 / currentValue;

  let prevWidth = maxBarWidth / prevScale;
  let currWidth = maxBarWidth / currScale - prevWidth;

  if (variant === 'declined') {
    currWidth = maxBarWidth / currScale;
    prevWidth = maxBarWidth / prevScale - currWidth;
  }

  const groupClass = `
    transition-all duration-300 ease-in-out
    ${isDimmed ? 'opacity-30' : 'opacity-100'}
    ${isHovered ? '!stroke-[1.5px] !stroke-gray-500' : ''}
  `;

  return (
    <g
      onMouseEnter={() => setHoveredIndex(index)}
      onMouseLeave={() => setHoveredIndex(null)}
      className={groupClass}
    >
      {variant === 'declined' ? (
        <>
          <rect
            data-testid={COMPARISON_BAR_CHART_TEST_IDS.DECLINED.CURRENT}
            x={x}
            y={y}
            width={prevWidth}
            height={height}
            fill={currentColor}
          />
          <text x={x + 5} y={y + height / 2 + 5} fill="#44403C" fontSize={12}>
            {isEditCellSkeleton ? '' : currentValue}
          </text>

          <path
            data-testid={COMPARISON_BAR_CHART_TEST_IDS.DECLINED.PREV}
            d={`M${x + currWidth} ${y} 
              H${x + currWidth + prevWidth - 6} 
              A6 6 0 0 1 ${x + currWidth + prevWidth} ${y + 6}
              V${y + height - 6} 
              A6 6 0 0 1 ${x + currWidth + prevWidth - 6} ${y + height}
              H${x + currWidth} 
              Z`}
            fill={previousColor}
          />
          <text
            x={x + prevWidth + currWidth - 25}
            y={y + height / 2 + 5}
            fill="#44403C"
            fontSize={12}
          >
            {isEditCellSkeleton ? '' : previousValue}
          </text>
        </>
      ) : (
        <>
          <rect
            data-testid={COMPARISON_BAR_CHART_TEST_IDS.SUCCESS.PREV}
            x={x}
            y={y}
            width={prevWidth}
            height={height}
            fill={previousColor}
          />
          <text x={x + 5} y={y + height / 2 + 5} fill="#44403C" fontSize={12}>
            {isEditCellSkeleton ? '' : previousValue}
          </text>

          <path
            data-testid={COMPARISON_BAR_CHART_TEST_IDS.SUCCESS.CURRENT}
            d={`M ${x + prevWidth},${y}
                H ${x + prevWidth + currWidth - 6}
                A 6,6 0 0 1 ${x + prevWidth + currWidth},${y + 6}
                V ${y + height - 6}
                A 6,6 0 0 1 ${x + prevWidth + currWidth - 6},${y + height}
                H ${x + prevWidth}
                Z`}
            fill={currentColor}
          />
          <text
            x={x + prevWidth + currWidth - 25}
            y={y + height / 2 + 5}
            fill="#44403C"
            fontSize={12}
          >
            {isEditCellSkeleton ? '' : currentValue}
          </text>
        </>
      )}
    </g>
  );
};
